/**
 * PriorityRanker — permite ordenar las 4 prioridades del usuario.
 *
 * Interacción: click en cada opción para moverla al top del ranking.
 * La opción #1 aparece resaltada con color habita.
 */
import React, { useState } from 'react';
import { ArrowRight, GripVertical } from 'lucide-react';

const DEFAULT_ORDER = [
  { key: 'precio', label: 'Precio', desc: 'Encaje con tu presupuesto' },
  { key: 'ubicacion', label: 'Ubicación', desc: 'Zona, movilidad y trayecto' },
  { key: 'espacio', label: 'Espacio', desc: 'Área y habitaciones' },
  { key: 'tiempo', label: 'Tiempo (commute)', desc: 'Tiempo a tu ubicación clave' },
];

export default function PriorityRanker({ onSubmit, disabled }) {
  const [order, setOrder] = useState(DEFAULT_ORDER);

  const moveTo = (index, targetIndex) => {
    const copy = [...order];
    const [item] = copy.splice(index, 1);
    copy.splice(targetIndex, 0, item);
    setOrder(copy);
  };

  return (
    <div className="space-y-4" data-testid="priority-ranker">
      <ul className="space-y-2">
        {order.map((it, i) => (
          <li
            key={it.key}
            data-testid={`priority-item-${it.key}`}
            className={
              (i === 0
                ? 'border-habita bg-habita-mint/50'
                : 'border-gray-200 bg-white hover:border-habita/60') +
              ' rounded-2xl border-2 p-3 flex items-center gap-3 transition-all'
            }
          >
            <div
              className={
                (i === 0 ? 'bg-habita text-white' : 'bg-gray-100 text-habita-dark') +
                ' w-8 h-8 rounded-full grid place-items-center font-serif font-bold text-sm shrink-0'
              }
              data-testid={`priority-rank-${it.key}`}
            >
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-habita-dark">{it.label}</p>
              <p className="text-xs text-gray-500 leading-snug">{it.desc}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {i > 0 && (
                <button
                  onClick={() => moveTo(i, i - 1)}
                  disabled={disabled}
                  data-testid={`priority-up-${it.key}`}
                  className="text-xs w-8 h-8 rounded-full border border-gray-200 hover:border-habita text-habita-dark disabled:opacity-40"
                  aria-label={`Subir ${it.label}`}
                >
                  ↑
                </button>
              )}
              {i < order.length - 1 && (
                <button
                  onClick={() => moveTo(i, i + 1)}
                  disabled={disabled}
                  data-testid={`priority-down-${it.key}`}
                  className="text-xs w-8 h-8 rounded-full border border-gray-200 hover:border-habita text-habita-dark disabled:opacity-40"
                  aria-label={`Bajar ${it.label}`}
                >
                  ↓
                </button>
              )}
              <GripVertical className="w-4 h-4 text-gray-300 hidden sm:block" />
            </div>
          </li>
        ))}
      </ul>
      <button
        onClick={() => onSubmit(order.map((o) => o.key))}
        disabled={disabled}
        data-testid="priority-continue"
        className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-habita text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        Continuar con este orden <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
